import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";
import OwnerAvatar from "@/components/OwnerAvatar";
import AvatarPromptModal from "@/components/AvatarPromptModal";

/**
 * Sub-header shown at the top of a focus-area screen.
 * Desktop / TV: full padding, VIRTUS logo, subtitle, "OPERATOR / email" label.
 * Mobile (< md): compact — hides logo/subtitle/email text, keeps only the
 *   back-arrow, focus title, clickable avatar, and logout icon.
 */
export default function FocusHeader({ code, title, subtitle }) {
    const nav = useNavigate();
    const { logout, user } = useAuth();
    const [profileOpen, setProfileOpen] = useState(false);
    return (
        <div className="flex items-center justify-between px-3 md:px-8 py-2 md:py-5 border-b border-virtus-line bg-virtus-bg gap-2 md:gap-6 md:pr-24">
            <div className="flex items-center gap-3 md:gap-6 min-w-0 flex-1">
                <button
                    onClick={() => nav("/hub")}
                    className="flex items-center gap-1.5 md:gap-2 font-mono text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-orange transition-colors flex-shrink-0"
                    data-testid={TID.backToHubBtn}
                    aria-label="Back to hub"
                >
                    <ArrowLeft size={14} />
                    <span className="hidden md:inline">HUB</span>
                </button>
                <div className="hidden md:block h-6 w-px bg-virtus-line" />
                <img
                    src="/logos/virtus-white.png"
                    alt="VIRTUS"
                    className="hidden md:block h-8 w-auto"
                />
                <div className="hidden md:block h-6 w-px bg-virtus-line" />
                <div className="min-w-0">
                    <div className="font-mono text-[9px] md:text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-orange">
                        {code}
                    </div>
                    <div className="font-heading text-base md:text-2xl font-black tracking-tight uppercase text-white truncate">
                        {title}
                    </div>
                    {subtitle && (
                        <div className="hidden md:block text-xs text-virtus-dim mt-1">
                            {subtitle}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                {user && (
                    <>
                        <div className="hidden md:block text-right">
                            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-faint">
                                OPERATOR
                            </div>
                            <div className="font-mono text-xs text-white">{user.email}</div>
                        </div>
                        <button
                            onClick={() => setProfileOpen(true)}
                            className="flex items-center gap-2 px-1.5 py-1 md:px-2 md:py-1.5 border border-virtus-line hover:border-virtus-orange transition-colors"
                            title="Change your profile photo"
                            data-testid="focus-profile-btn"
                        >
                            <OwnerAvatar owner={user.email} size={22} showName={false} />
                        </button>
                    </>
                )}
                <button
                    onClick={() => {
                        logout();
                        nav("/login");
                    }}
                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-stuck px-2 py-2 md:px-3 md:py-2 border border-virtus-line hover:border-virtus-stuck"
                    data-testid={TID.logoutBtn}
                    aria-label="Log out"
                >
                    <LogOut size={12} />
                    <span className="hidden md:inline">LOGOUT</span>
                </button>
            </div>

            {profileOpen && (
                <AvatarPromptModal forceOpen onClose={() => setProfileOpen(false)} />
            )}
        </div>
    );
}
